import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { useTheme } from '@voilajsx/uikit/theme-provider';
import { Button } from '@voilajsx/uikit/button';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Moon, Sun, Info } from 'lucide-react';

const DarkMode = () => {
  const { mode, setMode, toggleMode } = useTheme();

  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">Dark Mode</h1>
          <p className="text-lg text-muted-foreground">
            UIKit provides built-in dark mode support. Learn how to enable and customize it.
          </p>
        </div>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Toggle Dark Mode</h2>

          <div className="p-6 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground mb-4">
              Current mode: <strong className="capitalize">{mode}</strong>
            </p>
            <div className="flex gap-3">
              <Button
                variant={mode === 'light' ? 'default' : 'outline'}
                onClick={() => setMode('light')}
              >
                <Sun className="w-4 h-4 mr-2" />
                Light
              </Button>
              <Button
                variant={mode === 'dark' ? 'default' : 'outline'}
                onClick={() => setMode('dark')}
              >
                <Moon className="w-4 h-4 mr-2" />
                Dark
              </Button>
              <Button variant="outline" onClick={toggleMode}>
                Toggle
              </Button>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Basic Implementation</h2>

          <CodePreview
            title="Toggle Button Component"
            code={`import { useTheme } from '@voilajsx/uikit/theme-provider';
import { Button } from '@voilajsx/uikit/button';
import { Moon, Sun } from 'lucide-react';

function DarkModeToggle() {
  const { mode, toggleMode } = useTheme();

  return (
    <Button variant="ghost" size="icon" onClick={toggleMode}>
      {mode === 'dark' ? (
        <Sun className="w-5 h-5" />
      ) : (
        <Moon className="w-5 h-5" />
      )}
    </Button>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Setting Default Mode</h2>

          <CodePreview
            title="ThemeProvider Configuration"
            code={`// Start in light mode
<ThemeProvider defaultMode="light">
  <App />
</ThemeProvider>

// Start in dark mode
<ThemeProvider defaultMode="dark">
  <App />
</ThemeProvider>

// Start with system preference
<ThemeProvider defaultMode="system">
  <App />
</ThemeProvider>`}
            language="tsx"
          />

          <Alert>
            <Info className="w-4 h-4" />
            <AlertDescription>
              When using "system" mode, UIKit automatically detects the user's OS preference using <code>prefers-color-scheme</code>.
            </AlertDescription>
          </Alert>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">How It Works</h2>
          <p className="text-muted-foreground">
            Dark mode is implemented using CSS custom properties and a class on the root element:
          </p>

          <CodePreview
            title="Generated HTML"
            code={`<!-- Light mode -->
<html class="light" data-theme="default">

<!-- Dark mode -->
<html class="dark" data-theme="default">`}
            language="html"
          />

          <CodePreview
            title="CSS Variables"
            code={`/* Light mode colors */
.light {
  --background: oklch(1 0 0);        /* White */
  --foreground: oklch(0.15 0 0);     /* Near black */
}

/* Dark mode colors */
.dark {
  --background: oklch(0.15 0.02 260); /* Dark gray */
  --foreground: oklch(0.95 0 0);      /* Near white */
}`}
            language="css"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Mode-Specific Styling</h2>
          <p className="text-muted-foreground">
            You can apply styles that only appear in certain modes:
          </p>

          <CodePreview
            title="Tailwind Dark Mode Classes"
            code={`// Different backgrounds per mode
<div className="bg-white dark:bg-gray-900">
  Content
</div>

// Different text colors
<p className="text-gray-900 dark:text-gray-100">
  Adapts to mode
</p>

// Different borders
<div className="border-gray-200 dark:border-gray-700">
  Card
</div>

// Hide/show based on mode
<Sun className="dark:hidden" />
<Moon className="hidden dark:block" />`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Preview Comparison</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <Card className="bg-white text-gray-900 border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sun className="w-4 h-4" />
                  Light Mode
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  <Button size="sm" className="mt-3 bg-gray-900 text-white hover:bg-gray-800">
                    Button
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 text-gray-100 border-gray-700">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-gray-100">
                  <Moon className="w-4 h-4" />
                  Dark Mode
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-700 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-700 rounded w-1/2"></div>
                  <Button size="sm" className="mt-3 bg-white text-gray-900 hover:bg-gray-100">
                    Button
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Best Practices</h2>

          <ul className="space-y-3 text-muted-foreground">
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">1.</span>
              <div>
                <strong className="text-foreground">Use semantic colors</strong> - Prefer <code>bg-background</code> over <code>bg-white dark:bg-gray-900</code>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">2.</span>
              <div>
                <strong className="text-foreground">Test both modes</strong> - Always verify your UI looks good in both light and dark
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">3.</span>
              <div>
                <strong className="text-foreground">Respect user preference</strong> - Consider defaulting to system mode
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">4.</span>
              <div>
                <strong className="text-foreground">Avoid flashing</strong> - UIKit handles this, but custom implementations need to load theme before render
              </div>
            </li>
          </ul>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default DarkMode;
